import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { freight } from "../../lib/freightApi.js";
import { summaryLine } from "../recruitingView.js";
import { useCarrier } from "./CarrierApp.jsx";
import { Shell, Back, Title, Section, RowCard, ListCard, ListRow, Quiet, Note, when, errText, plural } from "./ui.jsx";
import { callWords } from "./hiringView.js";

/**
 * Hiring (the schematic's Hiring board): the applicants Alfred has talked to who are waiting on a
 * recruiter, and a way to everyone else. The groups come from the server (backend recruiting.mjs
 * hiringBoard), newest first.
 */

/** One applicant's line: what the application says, and the last call. */
export const applicantLine = (a) => [summaryLine(a), a.lastCall ? callWords(a.lastCall, when) : null].filter(Boolean).join(". ").replace(/\.\./g, ".");

export function HiringView({ company, stage, board, error, note }) {
  const ready = board?.ready?.rows || [];
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier">Home</Back>
      <Title>Hiring</Title>
      <Note msg={note ? { ok: true, text: note } : null} />
      {error && <Quiet>{`The applicants could not be read: ${error}`}</Quiet>}
      {!board && !error && <Quiet>Loading…</Quiet>}
      {board && (
        <>
          <Section id="ready-title" title={`Ready for a recruiter (${board.ready.count})`}>
            {ready.length === 0 && <Quiet>Nobody is waiting on a recruiter.</Quiet>}
            {ready.map((a) => <RowCard key={a.id} title={a.name} sub={applicantLine(a)} to={`/carrier/hiring/${a.id}`} label={`Open ${a.name}`} />)}
            {board.ready.count > ready.length && <Quiet>{`And ${board.ready.count - ready.length} more, oldest last.`}</Quiet>}
          </Section>
          <Link to="/carrier/hiring/all" style={{ fontSize: 16, fontWeight: 500, textDecoration: "none" }}>
            {`Alfred is working on ${board.working.count} other ${plural(board.working.count, "applicant", "applicants")} →`}
          </Link>
        </>
      )}
    </Shell>
  );
}

export const GROUPS = [
  { key: "working", title: "Alfred is working on" },
  // New applicants Alfred will not call on its own: no answer, no consent, added by hand, or automatic calls off.
  { key: "notReached", title: "Not reached yet" },
  { key: "forward", title: "Moving forward" },
  { key: "hired", title: "Hired" },
  { key: "notFit", title: "Not a fit" },
  { key: "dnc", title: "Asked not to be contacted" },
  { key: "past", title: "Past applicants, not contacted yet" },
];

export function AllApplicantsView({ company, stage, board, error }) {
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/hiring">Hiring</Back>
      <Title>All applicants</Title>
      {error && <Quiet>{`The applicants could not be read: ${error}`}</Quiet>}
      {!board && !error && <Quiet>Loading…</Quiet>}
      {board && GROUPS.map((g) => {
        const group = board[g.key];
        if (!group?.count) return null;
        return (
          <Section key={g.key} id={`group-${g.key}`} title={`${g.title} (${group.count})`}>
            <ListCard label={g.title}>
              {group.rows.map((a, i) => (
                <ListRow key={a.id} to={`/carrier/hiring/${a.id}`} name={a.name} last={i === group.rows.length - 1}>
                  <span>{applicantLine(a) || "Nothing on the application yet"}</span>
                </ListRow>
              ))}
            </ListCard>
            {group.count > group.rows.length && <Quiet>{`And ${group.count - group.rows.length} more.`}</Quiet>}
          </Section>
        );
      })}
      {board && GROUPS.every((g) => !board[g.key]?.count) && <Quiet>No other applicants.</Quiet>}
    </Shell>
  );
}

function useHiringBoard() {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    freight.hiring().then((b) => alive && setBoard(b)).catch((e) => alive && setError(errText(e)));
    return () => { alive = false; };
  }, []);
  return { board, error };
}

export default function Hiring() {
  const { carrier } = useCarrier();
  const { board, error } = useHiringBoard();
  const note = useLocation().state?.note || null;
  return <HiringView company={carrier.name} stage={carrier.stage} board={board} error={error} note={note} />;
}

export function AllApplicants() {
  const { carrier } = useCarrier();
  const { board, error } = useHiringBoard();
  return <AllApplicantsView company={carrier.name} stage={carrier.stage} board={board} error={error} />;
}
